import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../api";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, X, Download, TrendingUp, TrendingDown,
  History as HistoryIcon, Wallet, DollarSign, ChevronDown, ChevronRight, Eye, EyeOff,
} from "lucide-react";
import HistoryDrawer, { ImportCsvButton } from "../../components/HistoryDrawer";
import { useSortedList } from "../../components/useSortedList";
import { useConfirm } from "../../components/Confirm";
import { useDataVersion } from "../../hooks/useAppShell";

const VENDORS = ["DistroKid", "Madverse", "Firstwav", "YouTube", "Other"];
const USD_VENDORS = new Set(["YouTube", "DistroKid"]);

export default function Finance({ user }) {
  const [entries, setEntries] = useState([]);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [artists, setArtists] = useState([]);
  const [releases, setReleases] = useState([]);
  const loc = useLocation();
  const highlightId = new URLSearchParams(loc.search).get("highlight");
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="row-finance-${highlightId}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("entry-highlight"); setTimeout(() => el.classList.remove("entry-highlight"), 2600); }
    }, 350);
    return () => clearTimeout(t);
  }, [highlightId, entries]);

  const SORTABLE = [
    { key: "date", label: "Date" },
    { key: "description", label: "Description" },
    { key: "vendor", label: "Vendor / Platform" },
    { key: "type", label: "Type" },
    { key: "amount", label: "Amount (₹)" },
  ];
  // Build resolve maps so master search also matches by artist name / release title,
  // not just the UUIDs stored inside paid_for_artist_id and paid_for_song_id.
  const artistMap = {}; artists.forEach(a => { artistMap[a.id] = a.data?.name || a.id; });
  const releaseMap = {}; releases.forEach(r => { releaseMap[r.id] = r.title + (r.artist ? ` — ${r.artist}` : ""); });
  const { sorted: sortedEntries, SortBar, sortBarProps, Pagination, paginationProps } = useSortedList(entries, SORTABLE, "", { artistMap, releaseMap });
  const confirm = useConfirm();
  const [exportOpen, setExportOpen] = useState(false);
  const [vendorOpen, setVendorOpen] = useState(false);
  const [drillVendor, setDrillVendor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.entries.list("finance");
      setEntries(r.entries || []); setVersion(r.version || 0);
      const [a, rel] = await Promise.all([
        api.entries.list("artist_database").catch((err) => { console.error("artists load failed:", err); return { entries: [] }; }),
        api.linkedReleases().catch((err) => { console.error("releases load failed:", err); return []; }),
      ]);
      setArtists(a.entries || []);
      setReleases(rel || []);
    } catch (e) { toast.error(String(e.message || e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const globalVersion = useDataVersion();
  useEffect(() => { if (globalVersion > 0) load(); }, [globalVersion, load]);

  const totals = useMemo(() => {
    const t = { rev: 0, exp: 0, usdFees: 0, usdTax: 0, usdReceived: 0 };
    const byVendor = {};
    entries.forEach(e => {
      const d = e.data || {};
      const amt = Number(d.amount || 0);
      if (d.type === "Revenue") t.rev += amt;
      if (d.type === "Expense") t.exp += amt;
      if (d.type === "Revenue" && USD_VENDORS.has(d.vendor)) {
        t.usdFees += Number(d.transaction_fees_usd || 0);
        t.usdTax += Number(d.tax_withholding_usd || 0);
        t.usdReceived += Number(d.received_after_usd || 0);
      }
      const vk = d.vendor === "Other" ? (d.other_vendor_name || "Other") : (d.vendor || "Unassigned");
      if (!byVendor[vk]) byVendor[vk] = { revenue: 0, expense: 0, count: 0, usdReceived: 0 };
      byVendor[vk].count += 1;
      if (d.type === "Revenue") byVendor[vk].revenue += amt;
      if (d.type === "Expense") byVendor[vk].expense += amt;
      if (d.type === "Revenue" && USD_VENDORS.has(d.vendor)) byVendor[vk].usdReceived += Number(d.received_after_usd || 0);
    });
    return { ...t, net: t.rev - t.exp, byVendor };
  }, [entries]);

  const submit = async (data, id) => {
    try {
      if (id) await api.entries.update("finance", id, data, version);
      else await api.entries.create("finance", data);
      toast.success(id ? "Saved" : "Added");
      setShowAdd(false); setEditing(null); load();
    } catch (e) {
      if (String(e.message).includes("Someone else")) { toast.error("Refreshed — someone else made changes"); load(); }
      else toast.error(String(e.message || e));
    }
  };
  const remove = (id) => {
    confirm(
      "This finance row will move to Recently Deleted. You can restore it or delete it permanently within 15 days.",
      async () => {
        try { await api.entries.remove("finance", id); toast.success("Moved to Recently Deleted"); load(); }
        catch (e) { toast.error(String(e.message || e)); }
      },
      { title: "Delete this row?" }
    );
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>Department · Admin only</div>
          <h1 style={{ fontSize: 30, margin: "4px 0 0 0" }}>Finance</h1>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <ImportCsvButton dept="finance" onDone={load} />
          <button className="btn" onClick={() => setExportOpen(true)} data-testid="finance-csv-btn"><Download size={14} /> Export CSV</button>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="add-finance-btn"><Plus size={14} /> New entry</button>
        </div>
      </div>

      {exportOpen && <ExportDialog artists={artists} onClose={() => setExportOpen(false)} />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 16 }}>
        <Card label="Total Revenue" value={totals.rev} accent="mint" icon={<TrendingUp size={14} />} testid="fin-revenue" />
        <Card label="Total Expenses" value={totals.exp} accent="warn" icon={<TrendingDown size={14} />} testid="fin-expense" />
        <Card label="Net Position" value={totals.net} accent={totals.net >= 0 ? "mint" : "danger"} testid="fin-net" icon={<Wallet size={14} />} revealable />
      </div>

      {(totals.usdFees || totals.usdTax || totals.usdReceived) ? (
        <div className="panel" style={{ padding: 18, marginBottom: 16 }} data-testid="usd-breakdown">
          <div className="muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <DollarSign size={11} /> USD Deductions — YouTube &amp; DistroKid <span style={{ opacity: 0.6 }}>· reference only, not counted in ₹ totals</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
            <MiniStat label="Transaction Fees" value={`$ ${totals.usdFees.toLocaleString("en-IN")}`} tone="warn" />
            <MiniStat label="Tax Withholding" value={`$ ${totals.usdTax.toLocaleString("en-IN")}`} tone="warn" />
            <MiniStat label="Received After Deductions" value={`$ ${totals.usdReceived.toLocaleString("en-IN")}`} tone="mint" />
          </div>
        </div>
      ) : null}

      {Object.keys(totals.byVendor).length > 0 && (
        <div className="panel" style={{ marginBottom: 20 }} data-testid="by-vendor">
          <button
            type="button"
            onClick={() => setVendorOpen(o => !o)}
            data-testid="by-vendor-toggle"
            style={{
              width: "100%", background: "transparent", border: "none", cursor: "pointer",
              color: "var(--text)", padding: "14px 18px", display: "flex", alignItems: "center",
              gap: 8, justifyContent: "space-between",
            }}
          >
            <span className="muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
              {vendorOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              By Vendor / Platform
              <span className="badge" style={{ padding: "2px 8px", fontSize: 10, background: "var(--panel2)", color: "var(--muted)" }}>{Object.keys(totals.byVendor).length}</span>
            </span>
            <span className="muted" style={{ fontSize: 10.5 }}>{vendorOpen ? "Hide" : "Show"}</span>
          </button>
          {vendorOpen && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, padding: "0 18px 18px 18px" }}>
              {Object.entries(totals.byVendor).map(([vendor, v]) => (
                <button
                  key={vendor}
                  type="button"
                  onClick={() => setDrillVendor(vendor)}
                  data-testid={`vendor-${vendor}`}
                  className="clickable"
                  style={{ textAlign: "left", background: "var(--panel2)", border: "1px solid var(--border)", borderRadius: 10, padding: 14, cursor: "pointer", color: "var(--text)" }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{vendor}</div>
                  <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>{v.count} entr{v.count === 1 ? "y" : "ies"}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                    <span className="muted">Revenue</span>
                    <span style={{ color: "var(--mint)", fontFamily: "Space Grotesk", fontWeight: 600 }}>₹ {v.revenue.toLocaleString("en-IN")}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                    <span className="muted">Expense</span>
                    <span style={{ color: "var(--warn)", fontFamily: "Space Grotesk", fontWeight: 600 }}>₹ {v.expense.toLocaleString("en-IN")}</span>
                  </div>
                  {v.usdReceived > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, borderTop: "1px dashed var(--border)", paddingTop: 4, marginTop: 4 }}>
                      <span className="muted">USD received</span>
                      <span style={{ color: "var(--mint)" }}>$ {v.usdReceived.toLocaleString("en-IN")}</span>
                    </div>
                  )}
                  <div className="muted" style={{ fontSize: 10, marginTop: 8, opacity: 0.7 }}>Click to see every entry →</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {drillVendor && (
        <VendorDrillDown
          vendor={drillVendor}
          entries={entries.filter(e => {
            const d = e.data || {};
            const vk = d.vendor === "Other" ? (d.other_vendor_name || "Other") : (d.vendor || "Unassigned");
            return vk === drillVendor;
          })}
          onClose={() => setDrillVendor(null)}
        />
      )}

      {(showAdd || editing) && (
        <FinanceForm
          initial={editing?.data || {}}
          onCancel={() => { setShowAdd(false); setEditing(null); }}
          onSubmit={(d) => submit(d, editing?.id)}
          isEdit={!!editing}
          artists={artists}
          releases={releases}
        />
      )}

      <div className="panel" style={{ overflow: "hidden" }}>
        {loading ? <div style={{ padding: 20 }} className="muted">Loading…</div> :
         entries.length === 0 ? <div style={{ padding: 40, textAlign: "center" }} className="muted">No entries yet.</div> :
        (
          <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--border)", flexWrap: "wrap", gap: 12 }}>
            <SortBar {...sortBarProps} />
            <span className="muted" style={{ fontSize: 11 }} data-testid="entry-count">{paginationProps.total} entr{paginationProps.total === 1 ? "y" : "ies"}</span>
          </div>
          <table>
            <thead><tr><th>Date</th><th>Description</th><th>Vendor</th><th>Paid To / For</th><th>Type</th><th style={{ textAlign: "right" }}>Amount (₹)</th><th></th></tr></thead>
            <tbody>
              {sortedEntries.map(e => {
                const d = e.data || {};
                const vendorLabel = d.vendor === "Other" ? (d.other_vendor_name || "Other") : (d.vendor || "—");
                const paidLabel = d.type === "Expense"
                  ? [d.paid_to, artistLabel(artists, d.paid_for_artist_id), releaseLabel(releases, d.paid_for_song_id)].filter(Boolean).join(" · ")
                  : "—";
                return (
                  <tr key={e.id} data-testid={`row-finance-${e.id}`}>
                    <td className="muted" style={{ fontSize: 12 }}>{d.date || "—"}</td>
                    <td style={{ fontWeight: 500 }}>{d.description}</td>
                    <td>{vendorLabel}</td>
                    <td className="muted" style={{ fontSize: 12, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{paidLabel || "—"}</td>
                    <td><span className={`badge ${d.type === "Revenue" ? "badge-mint" : "badge-warn"}`}>{d.type}</span></td>
                    <td style={{ textAlign: "right", fontFamily: "Space Grotesk", fontWeight: 600, color: d.type === "Revenue" ? "var(--mint)" : "var(--warn)" }}>
                      {d.type === "Expense" ? "−" : "+"}{Number(d.amount || 0).toLocaleString("en-IN")}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="btn" onClick={() => setHistoryFor(e.id)} data-testid={`history-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }} title="Change history"><HistoryIcon size={12} /></button>{" "}
                      <button className="btn" onClick={() => setEditing(e)} data-testid={`edit-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }}><Pencil size={12} /></button>{" "}
                      <button className="btn btn-danger" onClick={() => remove(e.id)} data-testid={`del-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }}><Trash2 size={12} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination {...paginationProps} />
          </>
        )}
      </div>
      {historyFor && <HistoryDrawer entryId={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}

function artistLabel(artists, id) { const a = artists.find(x => x.id === id); return a ? a.data?.name : ""; }
function releaseLabel(rels, id) { const r = rels.find(x => x.id === id); return r ? `${r.title}${r.artist ? ` — ${r.artist}` : ""}` : ""; }

function ExportDialog({ onClose, artists = [] }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [artistId, setArtistId] = useState(""); // "" = All artists
  const [type, setType] = useState(""); // "" = Both, "Revenue", "Expense"
  // Track which vendors are selected. Empty set == all vendors.
  const [vendors, setVendors] = useState(new Set());
  const [paidTo, setPaidTo] = useState("");
  const toggleVendor = (v) => setVendors(prev => {
    const next = new Set(prev);
    if (next.has(v)) next.delete(v); else next.add(v);
    return next;
  });
  const download = () => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (artistId) params.set("artist_id", artistId);
    if (type) params.set("type", type);
    if (vendors.size > 0) params.set("vendors", Array.from(vendors).join(","));
    if (paidTo.trim()) params.set("paid_to", paidTo.trim());
    const url = api.financeCsvUrl() + (params.toString() ? "?" + params.toString() : "");
    const a = document.createElement("a");
    a.href = url; a.download = ""; document.body.appendChild(a); a.click(); a.remove();
    onClose();
  };
  const sortedArtists = [...artists].sort((a, b) =>
    (a.data?.name || "").localeCompare(b.data?.name || "")
  );
  // Vendor options match the picker in the finance form so what you select
  // here lines up with what you entered when creating the row.
  const VENDOR_OPTIONS = ["DistroKid", "Madverse", "Firstwav", "YouTube", "Other"];
  const activeFilterCount = (from?1:0) + (to?1:0) + (artistId?1:0) + (type?1:0) + (vendors.size>0?1:0) + (paidTo.trim()?1:0);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(2,5,9,0.75)", backdropFilter: "blur(4px)", overflowY: "auto" }} data-testid="export-dialog" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()} style={{ padding: 0, width: "min(560px, 96vw)", maxHeight: "90vh", overflowY: "auto", margin: "auto" }}>
        <div style={{ padding: "20px 22px 14px", position: "sticky", top: 0, zIndex: 2, background: "var(--panel)", borderBottom: "1px solid var(--border)", borderTopLeftRadius: "inherit", borderTopRightRadius: "inherit" }}>
          <div className="muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase" }}>Export Finance CSV</div>
          <h3 style={{ fontSize: 17, margin: "4px 0 0 0" }}>Build a filtered CSV</h3>
          <p className="muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.55 }}>Leave any filter blank to include everything. Combine filters to export something as narrow as "YouTube revenue paid to Noor between Jan-Mar". {activeFilterCount > 0 && <b style={{ color: "var(--mint)" }}>· {activeFilterCount} filter{activeFilterCount !== 1 ? "s" : ""} active</b>}</p>
        </div>

        <div style={{ padding: "18px 22px 22px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <label style={{ fontSize: 12 }}>
              <span className="muted" style={{ display: "block", marginBottom: 5 }}>From</span>
              <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="export-from" />
            </label>
            <label style={{ fontSize: 12 }}>
              <span className="muted" style={{ display: "block", marginBottom: 5 }}>To</span>
              <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="export-to" />
            </label>
          </div>

          <label style={{ fontSize: 12, display: "block", marginBottom: 14 }}>
            <span className="muted" style={{ display: "block", marginBottom: 5 }}>Type</span>
            <select className="input" value={type} onChange={(e) => setType(e.target.value)} data-testid="export-type">
              <option value="">Both — Revenue and Expense</option>
              <option value="Revenue">Revenue only</option>
              <option value="Expense">Expense only</option>
            </select>
          </label>

          <div style={{ fontSize: 12, marginBottom: 14 }}>
            <span className="muted" style={{ display: "block", marginBottom: 6 }}>Vendor / Platform <span style={{ opacity: 0.6 }}>· pick any number, leave empty for all</span></span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {VENDOR_OPTIONS.map(v => {
                const active = vendors.has(v);
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleVendor(v)}
                    data-testid={`export-vendor-${v}`}
                    style={{
                      padding: "6px 11px", fontSize: 11.5, borderRadius: 999,
                      background: active ? "var(--violet)" : "var(--panel2)",
                      border: "1px solid " + (active ? "var(--violet)" : "var(--border)"),
                      color: active ? "white" : "var(--text)",
                      cursor: "pointer", fontWeight: active ? 600 : 400,
                    }}
                  >
                    {active ? "✓ " : ""}{v}
                  </button>
                );
              })}
            </div>
          </div>

          <label style={{ fontSize: 12, display: "block", marginBottom: 14 }}>
            <span className="muted" style={{ display: "block", marginBottom: 5 }}>Paid To contains <span style={{ opacity: 0.6 }}>· case-insensitive</span></span>
            <input type="text" className="input" value={paidTo} onChange={(e) => setPaidTo(e.target.value)} placeholder="e.g. Noor, GST, Studio…" data-testid="export-paid-to" />
          </label>

          <label style={{ fontSize: 12, display: "block", marginBottom: 20 }}>
            <span className="muted" style={{ display: "block", marginBottom: 5 }}>Paid-For Artist <span style={{ opacity: 0.6 }}>· applies only to Expense rows</span></span>
            <select
              className="input"
              value={artistId}
              onChange={(e) => setArtistId(e.target.value)}
              data-testid="export-artist"
            >
              <option value="">All artists</option>
              {sortedArtists.map(a => (
                <option key={a.id} value={a.id}>{a.data?.name || a.id}</option>
              ))}
            </select>
          </label>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="btn" onClick={onClose} data-testid="export-cancel">Cancel</button>
            <button className="btn btn-primary" onClick={download} data-testid="export-download"><Download size={14} /> Download CSV</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, accent, icon, testid, revealable = false }) {
  const color = accent === "mint" ? "var(--mint)" : accent === "warn" ? "var(--warn)" : accent === "danger" ? "var(--danger)" : "var(--text)";
  const [revealed, setRevealed] = useState(!revealable);
  return (
    <div className="panel" style={{ padding: 18, position: "relative" }} data-testid={testid}>
      <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 6 }}>
        {icon} {label}
      </div>
      <div
        style={{
          fontFamily: "Space Grotesk", fontWeight: 700, fontSize: 26, marginTop: 8,
          color: revealed ? color : "var(--muted)",
          letterSpacing: revealed ? "normal" : "0.1em",
        }}
      >
        {revealed ? <>₹ {Math.round(value).toLocaleString("en-IN")}</> : "••••••"}
      </div>
      {revealable && (
        <button
          type="button"
          onClick={() => setRevealed(v => !v)}
          data-testid={`${testid}-reveal`}
          aria-label={revealed ? "Hide amount" : "Reveal amount"}
          title={revealed ? "Hide" : "Reveal"}
          style={{
            position: "absolute", top: 12, right: 12,
            background: "var(--panel2)", border: "1px solid var(--border)",
            borderRadius: 8, padding: "5px 7px", cursor: "pointer",
            color: "var(--muted)", display: "inline-flex", alignItems: "center",
          }}
        >
          {revealed ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
      )}
    </div>
  );
}
function MiniStat({ label, value, tone }) {
  const color = tone === "mint" ? "var(--mint)" : tone === "warn" ? "var(--warn)" : "var(--text)";
  return (
    <div style={{ background: "var(--panel2)", border: "1px solid var(--border)", borderRadius: 10, padding: 12 }}>
      <div className="muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}>{label}</div>
      <div style={{ fontFamily: "Space Grotesk", fontWeight: 700, fontSize: 20, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function FinanceForm({ initial, onSubmit, onCancel, isEdit, artists, releases }) {
  const [d, setD] = useState(initial);
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));
  const isRevenue = d.type === "Revenue";
  const isExpense = d.type === "Expense";
  const isUSD = isRevenue && USD_VENDORS.has(d.vendor);
  const isOtherVendor = d.vendor === "Other";

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...d };
    ["amount", "transaction_fees_usd", "tax_withholding_usd", "received_after_usd"].forEach(k => {
      if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]);
    });
    // Wipe out USD fields if vendor changed away
    if (!isUSD) {
      delete payload.transaction_fees_usd; delete payload.tax_withholding_usd; delete payload.received_after_usd;
    }
    // Wipe expense fields if type is Revenue
    if (isRevenue) {
      delete payload.paid_to; delete payload.paid_for_artist_id; delete payload.paid_for_song_id;
    }
    if (!isOtherVendor) delete payload.other_vendor_name;
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="panel" style={{ padding: 22, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
        <h3 style={{ fontSize: 16 }}>{isEdit ? "Edit entry" : "New entry"}</h3>
        <button type="button" className="btn" onClick={onCancel}><X size={14} /></button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
        <Field label="Description *"><input className="input" required value={d.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="field-description" /></Field>
        <Field label="Type *">
          <select className="input" required value={d.type || ""} onChange={(e) => set("type", e.target.value)} data-testid="field-type">
            <option value="">Select…</option><option>Revenue</option><option>Expense</option>
          </select>
        </Field>
        <Field label="Date"><input className="input" type="date" value={d.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="field-date" /></Field>
        <Field label="Amount (₹) *"><input className="input" type="number" step="0.01" required value={d.amount ?? ""} onChange={(e) => set("amount", e.target.value)} data-testid="field-amount" /></Field>
        <Field label="Vendor / Platform">
          <select className="input" value={d.vendor || ""} onChange={(e) => set("vendor", e.target.value)} data-testid="field-vendor">
            <option value="">Select…</option>
            {VENDORS.map(v => <option key={v}>{v}</option>)}
          </select>
        </Field>
        {isOtherVendor && (
          <Field label="Specify Vendor / Buyer">
            <input className="input" value={d.other_vendor_name || ""} onChange={(e) => set("other_vendor_name", e.target.value)} data-testid="field-other-vendor" />
          </Field>
        )}
      </div>

      {isUSD && (
        <div style={{ marginTop: 18, padding: 14, background: "var(--panel2)", borderRadius: 10, border: "1px dashed var(--border)" }}>
          <div className="muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <DollarSign size={11} /> USD Breakdown — {d.vendor}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            <Field label="Transaction Fees ($)"><input className="input" type="number" step="0.01" value={d.transaction_fees_usd ?? ""} onChange={(e) => set("transaction_fees_usd", e.target.value)} data-testid="field-transaction_fees_usd" /></Field>
            <Field label="Tax Withholding ($)"><input className="input" type="number" step="0.01" value={d.tax_withholding_usd ?? ""} onChange={(e) => set("tax_withholding_usd", e.target.value)} data-testid="field-tax_withholding_usd" /></Field>
            <Field label="Received After Deductions ($)"><input className="input" type="number" step="0.01" value={d.received_after_usd ?? ""} onChange={(e) => set("received_after_usd", e.target.value)} data-testid="field-received_after_usd" /></Field>
          </div>
        </div>
      )}

      {isExpense && (
        <div style={{ marginTop: 18, padding: 14, background: "var(--panel2)", borderRadius: 10, border: "1px dashed var(--border)" }}>
          <div className="muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>Expense Details</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <Field label="Paid To"><input className="input" value={d.paid_to || ""} onChange={(e) => set("paid_to", e.target.value)} data-testid="field-paid-to" /></Field>
            <Field label="Paid For (Artist)">
              <select className="input" value={d.paid_for_artist_id || ""} onChange={(e) => set("paid_for_artist_id", e.target.value)} data-testid="field-paid-artist">
                <option value="">— None —</option>
                {artists.map(a => <option key={a.id} value={a.id}>{a.data?.name || a.id}</option>)}
              </select>
            </Field>
            <Field label="Paid For (Song)">
              <select className="input" value={d.paid_for_song_id || ""} onChange={(e) => set("paid_for_song_id", e.target.value)} data-testid="field-paid-song">
                <option value="">— None —</option>
                {releases.map(r => <option key={r.id + r.source} value={r.id}>{r.title}{r.artist && ` — ${r.artist}`}</option>)}
              </select>
            </Field>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" data-testid="submit-entry">{isEdit ? "Save" : "Add"}</button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ fontSize: 12 }}>
      <span className="muted" style={{ display: "block", marginBottom: 5 }}>{label}</span>
      {children}
    </label>
  );
}

/**
 * VendorDrillDown — click a vendor card to see EVERY contributing entry.
 * Helps spot duplicates, wrong USD values, or missing deductions in one glance.
 * Also highlights entries where the ₹ Amount and $ Received look suspiciously close
 * (which usually means the user typed the ₹ number into the $ field by mistake).
 */
function VendorDrillDown({ vendor, entries, onClose }) {
  const sorted = [...entries].sort((a, b) => (b.data?.date || "").localeCompare(a.data?.date || ""));
  const rev = sorted.filter(e => (e.data || {}).type === "Revenue");
  const totalInr = rev.reduce((s, e) => s + Number(e.data?.amount || 0), 0);
  const totalUsdRecv = rev.reduce((s, e) => s + Number(e.data?.received_after_usd || 0), 0);
  const totalUsdFees = rev.reduce((s, e) => s + Number(e.data?.transaction_fees_usd || 0), 0);
  const totalUsdTax  = rev.reduce((s, e) => s + Number(e.data?.tax_withholding_usd  || 0), 0);
  return (
    <div
      onClick={onClose}
      data-testid="vendor-drilldown-backdrop"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1100, padding: 20, overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="panel modal-content"
        data-testid="vendor-drilldown"
        style={{ padding: 0, width: "100%", maxWidth: 760, maxHeight: "88vh", overflowY: "auto", margin: "auto" }}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
          padding: "18px 22px 14px",
          position: "sticky", top: 0, zIndex: 2,
          background: "var(--panel)",
          borderBottom: "1px solid var(--border)",
          borderTopLeftRadius: "inherit", borderTopRightRadius: "inherit",
        }}>
          <div>
            <div className="muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>Vendor breakdown</div>
            <h3 style={{ fontSize: 18, margin: 0 }}>{vendor}</h3>
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              {sorted.length} entries · {rev.length} revenue rows · <span style={{ color: "var(--mint)" }}>₹ {totalInr.toLocaleString("en-IN")}</span>
              {totalUsdRecv > 0 && <> · <span style={{ color: "var(--mint)" }}>$ {totalUsdRecv.toLocaleString("en-IN")} received</span></>}
            </div>
          </div>
          <button onClick={onClose} className="btn" data-testid="close-vendor-drilldown" aria-label="Close"><X size={14} /></button>
        </div>

        <div style={{ padding: "16px 22px 22px" }}>
        {totalUsdRecv > 0 && (
          <div style={{ background: "rgba(76,232,206,0.06)", border: "1px solid rgba(76,232,206,0.28)", borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 12, lineHeight: 1.55 }}>
            <div className="muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>USD Reference · does not affect ₹ totals</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
              <div><span className="muted">Fees $</span><br /><b>$ {totalUsdFees.toLocaleString("en-IN")}</b></div>
              <div><span className="muted">Tax $</span><br /><b>$ {totalUsdTax.toLocaleString("en-IN")}</b></div>
              <div><span className="muted">Received $</span><br /><b>$ {totalUsdRecv.toLocaleString("en-IN")}</b></div>
            </div>
          </div>
        )}

        <table>
          <thead>
            <tr>
              <th>Date</th><th>Description</th><th>Type</th>
              <th style={{ textAlign: "right" }}>Amount ₹</th>
              <th style={{ textAlign: "right" }}>Received $</th>
              <th style={{ textAlign: "right" }}>Fees $</th>
              <th style={{ textAlign: "right" }}>Tax $</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(e => {
              const d = e.data || {};
              const amt = Number(d.amount || 0);
              const usd = Number(d.received_after_usd || 0);
              // Suspicious data-entry check: if $ Received == ₹ Amount (or within 1),
              // the user almost certainly typed the ₹ number into the $ field.
              const suspicious = usd > 100 && Math.abs(usd - amt) <= 1;
              return (
                <tr key={e.id} data-testid={`drill-row-${e.id}`} style={suspicious ? { background: "rgba(255,107,107,0.08)" } : {}}>
                  <td className="muted" style={{ fontSize: 11 }}>{d.date || "—"}</td>
                  <td style={{ fontSize: 12, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>{d.description}</td>
                  <td><span className={`badge ${d.type === "Revenue" ? "badge-mint" : "badge-warn"}`} style={{ fontSize: 10 }}>{d.type}</span></td>
                  <td style={{ textAlign: "right", fontFamily: "Space Grotesk", fontSize: 12 }}>{amt ? amt.toLocaleString("en-IN") : "—"}</td>
                  <td style={{ textAlign: "right", fontFamily: "Space Grotesk", fontSize: 12, color: suspicious ? "var(--danger)" : "inherit" }}>
                    {usd ? usd.toLocaleString("en-IN") : "—"}
                    {suspicious && <span title="Received $ equals ₹ Amount — possibly a data-entry error" style={{ marginLeft: 4 }}>⚠</span>}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "Space Grotesk", fontSize: 12 }}>{d.transaction_fees_usd ? Number(d.transaction_fees_usd).toLocaleString("en-IN") : "—"}</td>
                  <td style={{ textAlign: "right", fontFamily: "Space Grotesk", fontSize: 12 }}>{d.tax_withholding_usd ? Number(d.tax_withholding_usd).toLocaleString("en-IN") : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="muted" style={{ fontSize: 11, marginTop: 12, lineHeight: 1.55 }}>
          Rows highlighted in red have <b>Received $</b> equal to <b>Amount ₹</b>. This almost always means the ₹ number was accidentally typed into the $ field — edit those entries from the main Finance list to fix.
        </p>
        </div>
      </div>
    </div>
  );
}
